
import React, { useState } from 'react';
import { 
  Download, 
  Music, 
  Video, 
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  FileVideo,
  FileAudio,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useYouTubeDownloadProgress } from '../../hooks/useYouTubeDownloadProgress';
import { cleanYouTubeUrl } from '../../utils/urlCleaner';
import GuestLimitReached from '../GuestLimitReached';
import UsageLimitIndicator from '../UsageLimitIndicator';

interface DownloadFormat {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  type: 'video' | 'audio';
  quality?: string;
  extension: string;
}

interface VideoInfo {
  title: string;
  thumbnail: string;
  duration: string;
  uploader: string;
  view_count?: number;
}

const YouTubeDownloader: React.FC = () => {
  const { currentUser } = useAuth();
  const { progress: downloadProgress, connectToDownload, disconnect: disconnectProgress, isConnected } = useYouTubeDownloadProgress();
  const [url, setUrl] = useState('');
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string>('mp4_720p');
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState('');
  const [isNoticeExpanded, setIsNoticeExpanded] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    downloadId: string;
    title: string;
  }>({ isOpen: false, downloadId: '', title: '' });
  const [usageLimits, setUsageLimits] = useState<any>(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);

  const formats: DownloadFormat[] = [
    // Video formats
    {
      id: 'mp4_2160p',
      name: '4K (2160p) MP4',
      description: 'Ultra high quality video',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '2160p',
      extension: 'mp4'
    },
    {
      id: 'mp4_1440p',
      name: '2K (1440p) MP4',
      description: 'Very high quality video',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '1440p',
      extension: 'mp4'
    },
    {
      id: 'mp4_1080p',
      name: '1080p MP4',
      description: 'High quality video',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '1080p',
      extension: 'mp4'
    },
    {
      id: 'mp4_720p',
      name: '720p MP4',
      description: 'Good quality video',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '720p',
      extension: 'mp4'
    },
    {
      id: 'mp4_480p',
      name: '480p MP4',
      description: 'Standard quality video',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '480p',
      extension: 'mp4'
    },
    {
      id: 'mp4_360p',
      name: '360p MP4',
      description: 'Low quality video (smaller file)',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '360p',
      extension: 'mp4'
    },
    {
      id: 'mp4_240p',
      name: '240p MP4',
      description: 'Very low quality (tiny file size)',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '240p',
      extension: 'mp4'
    },
    {
      id: 'mp4_144p',
      name: '144p MP4',
      description: 'Minimal quality (fastest, smallest)',
      icon: <Video className="w-5 h-5" />,
      type: 'video',
      quality: '144p',
      extension: 'mp4'
    },
    // Audio formats
    {
      id: 'mp3_320',
      name: 'MP3 320kbps',
      description: 'Highest quality audio',
      icon: <Music className="w-5 h-5" />,
      type: 'audio',
      quality: '320kbps',
      extension: 'mp3'
    },
    {
      id: 'mp3_256',
      name: 'MP3 256kbps',
      description: 'Very high quality audio',
      icon: <Music className="w-5 h-5" />,
      type: 'audio',
      quality: '256kbps',
      extension: 'mp3'
    },
    {
      id: 'mp3_192',
      name: 'MP3 192kbps',
      description: 'High quality audio',
      icon: <Music className="w-5 h-5" />,
      type: 'audio',
      quality: '192kbps',
      extension: 'mp3'
    },
    {
      id: 'mp3_128',
      name: 'MP3 128kbps',
      description: 'Standard quality audio',
      icon: <Music className="w-5 h-5" />,
      type: 'audio',
      quality: '128kbps',
      extension: 'mp3'
    },
    {
      id: 'mp3_96',
      name: 'MP3 96kbps',
      description: 'Good quality (smaller file)',
      icon: <Music className="w-5 h-5" />,
      type: 'audio',
      quality: '96kbps',
      extension: 'mp3'
    }
  ];

// Thumbnail component with fallback to icon
interface ThumbnailWithFallbackProps {
  src?: string;
  alt: string;
}

const ThumbnailWithFallback: React.FC<ThumbnailWithFallbackProps> = ({ src, alt }) => {
  const [imageError, setImageError] = useState(false);

  if (!src || imageError) {
    return (
      <div className="p-2 bg-red-100 rounded-lg flex items-center justify-center">
        <FileVideo className="w-4 h-4 text-red-600" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-9 object-cover rounded-lg border border-primary-200"
      onError={() => setImageError(true)}
    />
  );
};

// Custom delete confirmation modal
interface DeleteConfirmationModalProps {
  isOpen: boolean;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ 
  isOpen, 
  title, 
  onConfirm, 
  onCancel, 
  isDeleting 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Download</h3>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </div>
        </div>
        
        <p className="text-gray-700 mb-6">
          Are you sure you want to remove <span className="font-medium">"{title}"</span> from your download history?
        </p>
        
        <div className="flex space-x-3 justify-end">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

  const isValidYouTubeUrl = (url: string) => {
    if (!url || typeof url !== 'string') return false;
    
    const patterns = [
      // Standard YouTube URLs
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+(&.*)?$/,
      // YouTube short URLs
      /^https?:\/\/youtu\.be\/[\w-]+(\?.*)?$/,
      // YouTube embed URLs
      /^https?:\/\/(www\.)?youtube\.com\/embed\/[\w-]+(\?.*)?$/,
      // YouTube mobile URLs
      /^https?:\/\/m\.youtube\.com\/watch\?v=[\w-]+(&.*)?$/,
      // YouTube music URLs
      /^https?:\/\/music\.youtube\.com\/watch\?v=[\w-]+(&.*)?$/
    ];
    return patterns.some(pattern => pattern.test(url.trim()));
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    if (!isValidYouTubeUrl(url)) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    setIsProcessing(true);
    setProcessingStep('Fetching video information...');
    setProgress(20);

    try {
      // Clean the URL to remove playlist parameters and other unnecessary query parameters
      const cleanedUrl = cleanYouTubeUrl(url);
      
      // Try server-side method first (provides complete info including duration)
      setProcessingStep('Extracting video information...');
      
      try {
        const response = await fetch('/api/v1/tools/youtube-info', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: cleanedUrl })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to fetch video information');
        }

        const info = await response.json();
        setVideoInfo(info);
        setProgress(100);
        toast.success('Video information loaded successfully!');
        
      } catch (serverError) {
        // If server-side fails, try client-side as fallback (basic info only)
        // console.log('Server-side method failed, trying client-side fallback...', serverError);
        setProcessingStep('Trying alternative method...');
        
        // Extract video ID for basic info
        const videoId = extractVideoId(url);
        
        // Get basic metadata using YouTube's oEmbed API
        const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
        const oembedResponse = await fetch(oembedUrl);
        
        if (!oembedResponse.ok) {
          throw new Error('Video not found or private');
        }
        
        const oembedData = await oembedResponse.json();
        
        // Create VideoInfo object compatible with existing UI
        const info = {
          title: oembedData.title,
          duration: 'Unknown', // oEmbed doesn't provide duration
          thumbnail: oembedData.thumbnail_url,
          view_count: 0, // Not available via oEmbed
          uploader: oembedData.author_name || 'Unknown'
        };
        
        setVideoInfo(info);
        setProgress(100);
        toast.success('Video information loaded successfully (limited info)!');
      }

    } catch (error) {
      console.error('Error fetching video info:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load video information');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
      setProgress(0);
    }
  };

  const extractVideoId = (url: string): string => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    throw new Error('Invalid YouTube URL');
  };

  const handleDownload = async () => {
    if (!videoInfo || !url) {
      toast.error('Please load video information first');
      return;
    }

    setIsProcessing(true);
    setIsDownloading(true);
    setProgress(0);
    setDownloadUrl(null);
    setProcessingStep('Initializing download...');

    try {
      const format = formats.find(f => f.id === selectedFormat);
      setProcessingStep(`Preparing ${format?.type} download...`);

      // Skip client-side download for now - it's unreliable
      // Go straight to server-side method
      try {
        // This will immediately throw and go to server-side
        throw new Error('Using server-side method');
        
      } catch (clientError) {
        // If client-side fails, try server-side as fallback
        // console.log('Client-side download failed, trying server-side...', clientError);
        // setProcessingStep('Trying server-side download...'); // Hiding server-side attempt message
        
        // Clean the URL to remove playlist parameters
        const cleanedUrl = cleanYouTubeUrl(url);
        
        // Start the download request
        const response = await fetch('/api/v1/tools/youtube-download', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(currentUser && { 'Authorization': `Bearer ${await currentUser.getIdToken()}` })
          },
          body: JSON.stringify({ 
            url: cleanedUrl, 
            format: selectedFormat 
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          
          // Check if it's a guest limit error (429 status)
          if (response.status === 429 && !currentUser) {
            setShowGuestLimitModal(true);
            throw new Error(errorData.detail || 'Guest download limit reached');
          }
          
          throw new Error(errorData.detail || 'Download failed');
        }

        let result;
        try {
          const responseText = await response.text();
          result = JSON.parse(responseText);
          // console.log('📥 Download API response:', result);
        } catch (jsonError) {
          console.error('❌ Failed to parse JSON response:', jsonError);
          throw new Error('Invalid response from server');
        }
        
        // If we already have a download URL, the download is complete (cached or immediate)
        if (result.download_url) {
          // console.log('✅ Download URL received immediately:', result.download_url);
          setDownloadUrl(result.download_url);
          setProgress(100);
          setIsProcessing(false);
          setIsDownloading(false);
          toast.success('Download ready! Click the download button below.');
          
          // Show guest limit modal after successful download for guests
          if (!currentUser) {
            setTimeout(() => {
              setShowGuestLimitModal(true);
            }, 2000); // Show after 2 seconds to let them enjoy the success
          }
        } else {
          // Connect to WebSocket for real-time progress if we have a download_id but no URL yet
          if (result.download_id) {
            // console.log('🔌 Connecting to WebSocket for download ID:', result.download_id);
            connectToDownload(result.download_id);
            setProcessingStep('Connecting to download progress...');
          } else {
            console.error('❌ No download_id or download_url in response:', result);
            throw new Error('Invalid response: missing download information');
          }
        }
        
        // Refresh usage limits and download history after successful download
        fetchUsageLimits();
        if (currentUser) {
          fetchDownloadHistory();
        }
      }

    } catch (error) {
      console.error('Download error:', error);
      toast.error(error instanceof Error ? error.message : 'Download failed');
      setIsProcessing(false);
      setIsDownloading(false);
      setProgress(0);
      setProcessingStep('');
      disconnectProgress();
    } finally {
      // Additional cleanup in case of any remaining processing state
      setTimeout(() => {
        if (isProcessing && !downloadUrl) {
          setIsProcessing(false);
          setIsDownloading(false);
        }
      }, 5000);
    }
  };

  const clearAll = () => {
    setUrl('');
    setVideoInfo(null);
    setDownloadUrl(null);
    setProgress(0);
    setProcessingStep('');
    setIsDownloading(false);
  };

  const fetchUsageLimits = React.useCallback(async () => {
    if (isLoadingLimits) return;
    
    setIsLoadingLimits(true);
    try {
      const response = await fetch('/api/v1/tools/usage-limits', {
        headers: {
          ...(currentUser && { 'Authorization': `Bearer ${await currentUser.getIdToken()}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageLimits(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage limits:', error);
    } finally {
      setIsLoadingLimits(false);
    }
  }, [currentUser, isLoadingLimits]);

  const fetchDownloadHistory = React.useCallback(async () => {
    if (!currentUser || isLoadingHistory) return;
    
    setIsLoadingHistory(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/tools/download-history', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const history = await response.json();
        setDownloadHistory(history.downloads || []);
      }
    } catch (error) {
      console.error('Error fetching download history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentUser, isLoadingHistory]);

  const openDeleteConfirmation = (downloadId: string, title: string) => {
    setDeleteConfirmation({
      isOpen: true,
      downloadId,
      title
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      downloadId: '',
      title: ''
    });
  };

  const deleteDownloadHistory = async () => {
    if (!currentUser || !deleteConfirmation.downloadId) return;
    
    const { downloadId, title: _title } = deleteConfirmation;
    setDeletingItems(prev => new Set(prev).add(downloadId));
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/tools/download-history/${downloadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        // Remove the item from local state
        setDownloadHistory(prev => prev.filter(item => item.id !== downloadId));
        toast.success('Download removed from history');
        closeDeleteConfirmation();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete download');
      }
    } catch (error) {
      console.error('Error deleting download history:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete download');
    } finally {
      setDeletingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadId);
        return newSet;
      });
    }
  };

  // Load download history when user is logged in (only once per user change)
  React.useEffect(() => {
    if (currentUser && !isLoadingHistory) {
      fetchDownloadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Only depend on user ID to prevent excessive calls

  // Load usage limits on component mount (only once per user change)
  React.useEffect(() => {
    if (!isLoadingLimits) {
      fetchUsageLimits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Only depend on user ID to prevent excessive calls

  // Handle download progress and completion from WebSocket
  React.useEffect(() => {
    // Update local progress from WebSocket
    if (isConnected && downloadProgress.percent !== undefined) {
      setProgress(downloadProgress.percent);
      if (downloadProgress.message) {
        setProcessingStep(downloadProgress.message);
      }
    }

    // Handle download completion
    if (!downloadUrl && downloadProgress.download_url) {
      setDownloadUrl(downloadProgress.download_url);
      setProgress(100);
      setIsProcessing(false);
      setIsDownloading(false);
      toast.success('Download ready! Click the download button below.');

      if (currentUser) {
        fetchDownloadHistory();
      }

      disconnectProgress();
    }

    // Handle download errors
    if (downloadProgress.status === 'error') {
      setIsProcessing(false);
      setIsDownloading(false);
      toast.error(downloadProgress.message || 'Download failed');
      disconnectProgress();
    }
  }, [downloadProgress, downloadUrl, currentUser, fetchDownloadHistory, disconnectProgress, isConnected]);

  // Cleanup WebSocket on unmount
  React.useEffect(() => {
    return () => {
      disconnectProgress();
    };
  }, [disconnectProgress]);

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold tracking-tight text-gray-900 mb-2">
            Download YouTube Content
          </h2>
          <p className="text-primary-600">
            Choose your preferred format and quality for video or audio downloads
          </p>
        </div>

        <div className="space-y-6">
          {/* Account Required Banner */}
          {!currentUser && (
            <div className="bg-white border-2 border-red-500 rounded-xl p-6 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-2xl">🔒</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-black font-bold text-lg">Account Required to Download</h3>
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">NEW POLICY</span>
                  </div>
                  <p className="text-gray-800 text-sm mb-3">
                    Due to <strong className="text-red-600">excessive abuse from anonymous users</strong>, we now require a free account to use our tools. This helps us keep the service running for everyone.
                  </p>
                  <div className="flex items-center space-x-3">
                    <a
                      href="/signup"
                      className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 hover:shadow-lg transition-all text-center"
                    >
                      Create Free Account
                    </a>
                    <a
                      href="/login"
                      className="flex-1 bg-white border-2 border-black text-black px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all text-center"
                    >
                      Sign In
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Guest mode banner with usage indicator - NOW REMOVED */}
          {false && !currentUser && usageLimits && usageLimits.user_type === 'guest' && (
            <UsageLimitIndicator
              used={usageLimits.usage_limits.youtube_downloads?.used || 0}
              limit={usageLimits.usage_limits.youtube_downloads?.limit || 1}
              feature="YouTube Download"
              isGuest={true}
              resetTime="24 hours"
            />
          )}
          
          {/* Logged-in user usage indicator */}
          {currentUser && usageLimits && usageLimits.usage_limits.youtube_downloads && (
            <UsageLimitIndicator
              used={usageLimits.usage_limits.youtube_downloads?.used || 0}
              limit={usageLimits.usage_limits.youtube_downloads?.limit || 999}
              feature="YouTube Download"
              isGuest={false}
            />
          )}



          {/* URL Input */}
          <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enter YouTube URL</h3>
            <form onSubmit={handleUrlSubmit} className="space-y-4">
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-white transition-colors ${
                      !url.trim() 
                        ? 'border border-primary-200 focus:ring-red-500'
                        : isValidYouTubeUrl(url)
                          ? 'border border-red-300 focus:ring-red-500'
                          : 'border border-red-300 focus:ring-red-500'
                    }`}
                    disabled={isProcessing}
                  />
                  {url.trim() && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {isValidYouTubeUrl(url) ? (
                        <CheckCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isProcessing || !url.trim() || !isValidYouTubeUrl(url)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    isProcessing || !url.trim() || !isValidYouTubeUrl(url)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Load Video'
                  )}
                </button>
              </div>

              {isProcessing && processingStep && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">{processingStep}</p>
                </div>
              )}
            </form>
          </div>

          {/* Video Info */}
          {videoInfo && (
            <div className="bg-white rounded-xl border border-primary-100 p-6">
              <div className="flex items-start space-x-4">
                <img
                  src={videoInfo.thumbnail}
                  alt={videoInfo.title}
                  className="w-32 h-24 object-cover rounded-lg border border-primary-100"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {videoInfo.title}
                  </h3>
                  <div className="space-y-1 text-sm text-primary-600">
                    <p>By {videoInfo.uploader}</p>
                    <p>Duration: {videoInfo.duration}</p>
                    {videoInfo.view_count && (
                      <p>{videoInfo.view_count.toLocaleString()} views</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Format Selection & Download */}
          {videoInfo && (
            <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Format & Quality</h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Video Formats */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <FileVideo className="w-4 h-4 mr-2" />
                    Video (MP4)
                  </h4>
                  <div className="space-y-2">
                    {formats.filter(f => f.type === 'video').map((format) => (
                      <label
                        key={format.id}
                        className={`flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedFormat === format.id
                            ? 'border-red-500 bg-red-50'
                            : 'border-primary-200 bg-white hover:border-red-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="format"
                          value={format.id}
                          checked={selectedFormat === format.id}
                          onChange={(e) => setSelectedFormat(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-2 flex-1">
                          <div className={`p-1.5 rounded-lg ${
                            selectedFormat === format.id ? 'bg-red-500 text-white' : 'bg-primary-100 text-primary-600'
                          }`}>
                            {format.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm">{format.name}</p>
                            <p className="text-xs text-gray-600 truncate">{format.description}</p>
                          </div>
                        </div>
                        {selectedFormat === format.id && (
                          <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Audio Formats */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <FileAudio className="w-4 h-4 mr-2" />
                    Audio (MP3)
                  </h4>
                  <div className="space-y-2">
                    {formats.filter(f => f.type === 'audio').map((format) => (
                      <label
                        key={format.id}
                        className={`flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedFormat === format.id
                            ? 'border-red-500 bg-red-50'
                            : 'border-primary-200 bg-white hover:border-red-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="format"
                          value={format.id}
                          checked={selectedFormat === format.id}
                          onChange={(e) => setSelectedFormat(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-2 flex-1">
                          <div className={`p-1.5 rounded-lg ${
                            selectedFormat === format.id ? 'bg-red-500 text-white' : 'bg-primary-100 text-primary-600'
                          }`}>
                            {format.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm">{format.name}</p>
                            <p className="text-xs text-gray-600 truncate">{format.description}</p>
                          </div>
                        </div>
                        {selectedFormat === format.id && (
                          <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Download Action - Integrated */}
              <div className="border-t border-primary-200 pt-4">
                {!downloadUrl && !isProcessing && (
                  <button
                    onClick={handleDownload}
                    disabled={!selectedFormat}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      selectedFormat 
                        ? 'bg-red-500 text-white hover:bg-red-600' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {selectedFormat ? 'Start Download' : 'Select a format to continue'}
                  </button>
                )}

                {isProcessing && (
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-4">
                      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
                    </div>
                    {/* <p className="text-lg font-medium text-gray-900 mb-2">Preparing Download...</p> */}
                    <p className="text-sm text-primary-600 mb-2">
                      {processingStep || 'Preparing download...'}
                    </p>
                    <div className="bg-primary-100 rounded-lg h-2">
                      <div
                        className={`bg-red-500 h-2 transition-all duration-300 ${progress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                        style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{Math.round(progress)}% Complete</span>
                      {isConnected && downloadProgress.speed_mb && downloadProgress.speed_mb > 0 && (
                        <span>{downloadProgress.speed_mb.toFixed(1)} MB/s</span>
                      )}
                    </div>
                    {isConnected && downloadProgress.total_mb && (
                      <p className="text-xs text-gray-500 mt-1">
                        {downloadProgress.downloaded_mb?.toFixed(1) || 0} MB / {downloadProgress.total_mb.toFixed(1)} MB
                        {downloadProgress.eta && downloadProgress.eta > 0 && (
                          <span> • ETA: {Math.round(downloadProgress.eta)}s</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {downloadUrl && (
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <p className="text-lg font-medium text-gray-900">Download Ready!</p>
                    <p className="text-sm text-gray-600 mb-4">Click the button below to download your file.</p>
                    
                    <div className="space-y-2">
                      <button
                        onClick={async () => {
                          if (downloadUrl && !isDownloading) {
                            setIsDownloading(true);
                            try {
                              const urlParts = downloadUrl.split('/');
                              const filename = urlParts[urlParts.length - 1] || `youtube-download-${Date.now()}.mp4`;
                              
                              const response = await fetch(downloadUrl);
                              const blob = await response.blob();
                              
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = decodeURIComponent(filename);
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                              
                              toast.success('Download started! Check your downloads folder.');
                              
                              // Refresh download history for logged-in users (with small delay)
                              if (currentUser) {
                                setTimeout(() => {
                                  fetchDownloadHistory();
                                }, 1000);
                              }
                            } catch (error) {
                              console.error('Download error:', error);
                              // Fallback
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = '';
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Download link opened.');
                              
                              // Refresh download history for logged-in users (with small delay)
                              if (currentUser) {
                                setTimeout(() => {
                                  fetchDownloadHistory();
                                }, 1000);
                              }
                            } finally {
                              setIsDownloading(false);
                            }
                          }
                        }}
                        disabled={isDownloading}
                        className={`inline-flex items-center py-3 px-6 rounded-lg font-medium transition-colors w-full justify-center ${
                          isDownloading 
                            ? 'bg-red-400 text-white cursor-not-allowed' 
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Download File
                          </>
                        )}
                      </button>
                      <button
                        onClick={clearAll}
                        className="block w-full text-primary-600 hover:text-gray-800 text-sm py-2"
                      >
                        Download another video
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Download History - For logged-in users */}
          {currentUser && (
            <div className="bg-primary-50/50 rounded-xl border border-primary-100 overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileVideo className="w-5 h-5 mr-2" />
                  Your Download History
                </h3>
                
                {isLoadingHistory ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Loading your downloads...</p>
                  </div>
                ) : downloadHistory.length > 0 ? (
                  <div className="space-y-3">
                    {downloadHistory.slice(0, 5).map((download, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-primary-200"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <ThumbnailWithFallback 
                            src={download.thumbnail}
                            alt={download.title || 'Video thumbnail'}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">
                              {download.title || 'YouTube Download'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {download.format} • {new Date(download.created_at).toLocaleDateString()}
                            </p>
                            {download.uploader && (
                              <p className="text-xs text-gray-400">
                                by {download.uploader}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {download.download_url && (
                            <button
                            onClick={() => {
                              try {
                                const urlParts = download.download_url.split('/');
                                const filename = urlParts[urlParts.length - 1] || `${download.title || 'download'}.mp4`;
                                
                                fetch(download.download_url)
                                  .then(response => response.blob())
                                  .then(blob => {
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = decodeURIComponent(filename);
                                    link.style.display = 'none';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(blobUrl);
                                    toast.success('Download started!');
                                  })
                                  .catch(() => {
                                    const link = document.createElement('a');
                                    link.href = download.download_url;
                                    link.download = '';
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  });
                              } catch (error) {
                                console.error('Re-download error:', error);
                              }
                            }}
                            className="p-2 text-gray-700 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download again"
                            aria-label="Download again"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          )}
                          <button
                            onClick={() => openDeleteConfirmation(download.id, download.title || 'Unknown')}
                            disabled={deletingItems.has(download.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              deletingItems.has(download.id)
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-500 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title="Delete from history"
                            aria-label={deletingItems.has(download.id) ? "Deleting..." : "Delete from history"}
                          >
                            {deletingItems.has(download.id) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    {downloadHistory.length > 5 && (
                      <p className="text-xs text-gray-500 text-center pt-2">
                        Showing 5 most recent downloads
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileVideo className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No downloads yet</p>
                    <p className="text-sm text-gray-500">Your YouTube downloads will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legal Notice - Collapsible */}
          <div className="bg-primary-50/50 rounded-xl border border-primary-100 overflow-hidden">
            <button
              onClick={() => setIsNoticeExpanded(!isNoticeExpanded)}
              className="w-full p-4 flex items-center justify-between hover:bg-primary-100/50 transition-colors"
            >
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">Important Legal Notice</h3>
              </div>
              {isNoticeExpanded ? (
                <ChevronUp className="w-5 h-5 text-primary-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-primary-600" />
              )}
            </button>
            
            <div className={`transition-all duration-300 ease-in-out ${
              isNoticeExpanded 
                ? 'max-h-96 opacity-100' 
                : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className="px-4 pb-4 border-t border-primary-100">
                <div className="space-y-2 text-sm text-primary-700 mt-3">
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Only download content you have permission to use
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Respect copyright laws and YouTube's Terms of Service
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    This tool is for personal use and educational purposes
                  </p>
                  <p className="flex items-start">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Downloaded files are automatically deleted after 24 hours
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-primary-200">
                  <a
                    href="https://www.youtube.com/howyoutubeworks/policies/copyright/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-red-600 hover:text-red-800 transition-colors"
                  >
                    Learn about YouTube's copyright policies
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title={deleteConfirmation.title}
        onConfirm={deleteDownloadHistory}
        onCancel={closeDeleteConfirmation}
        isDeleting={deletingItems.has(deleteConfirmation.downloadId)}
      />
      
      {/* Guest Limit Reached Modal */}
      {showGuestLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <GuestLimitReached 
            feature="download" 
            onClose={() => setShowGuestLimitModal(false)}
          />
        </div>
      )}
    </div>
  );
};

export default YouTubeDownloader;
